import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import endPoints from '../api/endpoint';
import { deleteApiCall, getApiCall, postApiCall } from '../api/methods';
import { notify } from '../Utils/toastify';

import store from './store';

interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  accountType: string;
  secCode: string;
  isVerified: boolean;
  isDefault: boolean;
  bankAccountId: string;
}

interface CardDetails {
  id: string;
  cardId: string;
  name: string;
  last4: string;
  brand: string;
  exp_month: number;
  exp_year: number;
  cardExpired: boolean;
  isDefault: boolean;
}

interface AchState {
  publicToken: string | null;
  bankAccountList: BankAccount[];
  savedCards: {
    data: CardDetails[];
  };
  loading: boolean;
}

const initialState: AchState = {
  publicToken: null,
  bankAccountList: [],
  savedCards: {
    data: [],
  },
  loading: false,
};

const achSlice = createSlice({
  name: 'ach',
  initialState,
  reducers: {
    setSavedCards: (state, action: PayloadAction<CardDetails[]>) => {
      state.savedCards.data = action.payload;
    },
    setPublicToken: (state, action: PayloadAction<string>) => {
      state.publicToken = action.payload;
    },
    setBankAccountList: (state, action: PayloadAction<BankAccount[]>) => {
      state.bankAccountList = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
});

export const { setSavedCards, setPublicToken, setBankAccountList, setLoading } =
  achSlice.actions;
export default achSlice.reducer;

const handleError = async (e: any) => {
  if (e.data && e.data.message) {
    notify(e.data.message, 'error');
  }
};

export const getSavedCards = (query?: string, cb?: any, errorCb?: any) => {
  const { dispatch } = store;
  dispatch(setLoading(true));
  getApiCall(
    `${endPoints.payment.getSavedCards}${query}`,
    (response: any) => {
      const { data } = response?.data || {};
      dispatch(setSavedCards(data?.data || []));
      cb && cb(data);
      dispatch(setLoading(false));
    },
    (error: any) => {
      handleError(error);
      errorCb && errorCb(error);
      dispatch(setLoading(false));
    },
  );
};

export const saveNewCard = (payload: any, cb?: any, errorCb?: any) => {
  postApiCall(
    `${endPoints.payment.saveCard}`,
    payload,
    (response: any) => {
      const { data } = response?.data || {};
      cb && cb(data);
    },
    (error: any) => {
      handleError(error);
      errorCb && errorCb(error);
    },
  );
};

export const deleteSaveCard = (id: string, cb?: any, errorCb?: any) => {
  deleteApiCall(
    `${endPoints.payment.getSavedCards}`,
    { cardId: id },
    (response: any) => {
      const { message } = response?.data || {};
      notify(message, 'success');
      cb && cb();
    },
    (error: any) => {
      handleError(error);
      errorCb && errorCb(error);
    },
  );
};

export const getPlaidTokenAction = (payload: any, cb?: any, errorCb?: any) => {
  const { dispatch } = store;
  postApiCall(
    `${endPoints.payment.getPlaidToken}`,
    payload,
    (response: any) => {
      const { data } = response?.data || {};
      dispatch(setPublicToken(data?.link_token));
      cb && cb(data);
    },
    (error: any) => {
      handleError(error);
      errorCb && errorCb(error);
    },
  );
};

export const getSavedBankAccount = (
  query?: string,
  cb?: any,
  errorCb?: any,
) => {
  const { dispatch } = store;
  dispatch(setLoading(true));
  getApiCall(
    `${endPoints.payment.getSavedBankAccounts}${query}`,
    (response: any) => {
      const { data } = response?.data || {};
      dispatch(setBankAccountList(data || []));
      cb && cb(data);
      dispatch(setLoading(false));
    },
    (error: any) => {
      handleError(error);
      errorCb && errorCb(error);
      dispatch(setLoading(false));
    },
  );
};

export const addBankAccount = (payload: any, cb?: any, errorCb?: any) => {
  postApiCall(
    `${endPoints.payment.addBankAccount}`,
    payload,
    (response: any) => {
      const { data } = response?.data || {};
      cb && cb(data);
    },
    (error: any) => {
      handleError(error);
      errorCb && errorCb(error);
    },
  );
};

export const paymentAchAction = (
  payload: any,
  cb?: any,
  errorCb?: any,
  endpoint?: string,
) => {
  postApiCall(
    endpoint === 'INVOICE'
      ? endPoints.payment.invoiceACHPayment
      : endPoints.payment.makeAchPayment,
    payload,
    (response: any) => {
      const { data } = response?.data || {};
      cb && cb(data);
    },
    (error: any) => {
      handleError(error);
      errorCb && errorCb(error);
    },
  );
};

export const deleteBankAccount = (payload: any, cb?: any, errorCb?: any) => {
  deleteApiCall(
    `${endPoints.payment.deleteBankAccount}`,
    { bankAccountId: payload },
    (response: any) => {
      notify('Bank account deleted successfully', 'success');
      cb && cb(response?.data);
    },
    (error: any) => {
      handleError(error);
      errorCb && errorCb(error);
    },
  );
};

export const makeCardPayment = (
  payload: any,
  cb?: any,
  errorCb?: any,
  endpoint?: string,
) => {
  // Clean payload - only include either cardId or token, not both
  const cleanPayload = { ...payload };
  if (cleanPayload?.cardId) {
    delete cleanPayload.token;
  }
  if (cleanPayload?.token) {
    delete cleanPayload.cardId;
  }

  postApiCall(
    endpoint === 'INVOICE'
      ? endPoints.payment.invoiceCardPayment
      : endPoints.payment.makeCardPayment,
    cleanPayload,
    (response: any) => {
      const { data } = response?.data || {};
      cb && cb(data);
    },
    (error: any) => {
      handleError(error);
      errorCb && errorCb(error);
    },
  );
};
